import {
  AgGrid,
  ErrorMessage,
  Spinner,
  InputField,
  Heading,
  Button,
  InfoBox,
  SuccessPopup,
  MobileResponsiveGrid,
} from "@/common/components";
import { useEffect, useContext, useState, useMemo } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetAllInsuranceCompaniesQuery,
  useGetProductInsuranceCompaniesQuery,
  useSaveProductInsuranceCompaniesMutation,
} from "@/service/ProductService";
import {
  addPercentage,
  getFormattedDate,
  scrollToTop,
} from "@/utils/Utilities";

export default function InsuranceCompanies() {
  const {
    labels,
    setNavHeader,
    setPageHeader,
    location,
    remove,
    cloneDeep,
    cancel,
    isRiccAdministrator,
    actionsDropdown,
    sortBy,
    accessorPrivileges,
  } = useContext(PrivateContext);

  const [selectedInsuranceFromAll, setSelectedInsuranceFromAll] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [rowData, setRowData] = useState([]);
  const [rowDataForAll, setRowDataForAll] = useState([]);
  const [currentCoverage, setCurrentCoverage] = useState(0);
  const [risk, setRisk] = useState("100");
  const [errors, setErrors] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const columnDefsForAll = [
    {
      headerName: labels?.productInsurer?.insuranceCompany,
      field: "companyName",
    },
    {
      headerName: labels?.productInsurer?.insurerTransmitterID,
      field: "id",
    },
    {
      headerName: labels?.productInsurer?.address,
      field: "addressLine1",
    },
    {
      headerName: labels?.productInsurer?.cityHeader,
      field: "addressCityName",
    },
    {
      headerName: labels?.productInsurer?.state,
      field: "addressStateCountry.longName",
    },
    {
      headerName: labels?.productInsurer?.zipCodeHeader,
      field: "zip",
    },
  ];

  const columnDefsForInsurance = useMemo(
    () => [
      {
        headerName: labels?.productInsurer?.riskPercentage,
        field: "riskPercentRate",
        cellRenderer: addPercentage,
        flex: 1.5,
      },
      {
        headerName: labels?.productInsurer?.insuranceCompany,
        field: "insuranceCompany.companyName",
        flex: 2.5,
      },
      {
        headerName: labels?.productInsurer?.actions,
        colId: "actions",
        cellRenderer: actionsDropdown,
        cellRendererParams: {
          list: [
            {
              text: labels?.productInsurer?.remove,
              click: (e) => removeInsurance(e),
              disabled:
                accessorPrivileges["INSURANCE PURCHASE / PAYMENT TABLES"] ===
                "RETRIEVE",
            },
          ],
        },
        flex: 1.25,
        sortable: false,
        hide:
          (accessorPrivileges["PRODUCT"] !== "ADD" ||
            accessorPrivileges["PRODUCT"] !== "UPDATE") &&
          !isRiccAdministrator,
      },
      {
        headerName: labels?.productInsurer?.insurerTransmitterID,
        field: "insuranceCompany.id",
        flex: 2,
      },
      {
        headerName: labels?.productInsurer?.addRemainderTo,
        field: "remainderTo",
        cellRenderer: "agCheckboxCellRenderer",
        flex: 2,
        editable: true,
        cellClass: "ms-5",
        headerClass: "text-center",
        cellRendererParams: {
          onChange: ({ next, rowIndex, field }) => {
            const updated = [...rowData];
            const curr = {
              ...updated[rowIndex],
              [field]: next,
              modified: true,
            };
            updated[rowIndex] = curr;
            setRowData(updated);
          },
        },
      },
      {
        headerName: labels?.productInsurer?.address,
        field: "insuranceCompany.addressLine1",
        flex: 2,
      },
      {
        headerName: labels?.productInsurer?.cityHeader,
        field: "insuranceCompany.addressCityName",
        flex: 2,
      },
      {
        headerName: labels?.productInsurer?.state,
        field: "insuranceCompany.addressStateCountry.longName",
        flex: 2,
      },
      {
        headerName: labels?.productInsurer?.zipCodeHeader,
        field: "insuranceCompany.zip",
        flex: 2,
      },
      {
        headerName: labels?.productInsurer?.date,
        field: "effectiveDate",
        flex: 2,
      },
    ],
    [accessorPrivileges, isRiccAdministrator, rowDataForAll, rowData]
  );

  const {
    data: allInsuranceCompanies,
    isFetching,
    error,
  } = useGetAllInsuranceCompaniesQuery({
    productSponsorId: location?.state?.productSponsorId,
  });

  const {
    data: insuranceCompanies,
    isFetching: isFetching1,
    error: error1,
  } = useGetProductInsuranceCompaniesQuery({
    productSponsorId: location?.state?.productSponsorId,
    productId: location?.state?.productId,
  });

  const [
    saveProductInsuranceCompaniesApi,
    { isLoading: isFetching2, error: error2 },
  ] = useSaveProductInsuranceCompaniesMutation();

  const readOnlyAccess = !(
    accessorPrivileges["PRODUCT"] === "ADD" ||
    accessorPrivileges["PRODUCT"] === "UPDATE" ||
    isRiccAdministrator
  );

  const getRowClassForAll = (params) => {
    if (params.data.id === selectedInsuranceFromAll) {
      return "hover-bg";
    }
    return "";
  };

  const onRowClickedForAll = (e) => {
    setSelectedInsuranceFromAll(e.data.id);
    setRisk("100");
    setErrors(null);
  };

  const hasValidNetworkConfiguration = (insurance) => {
    return insurance?.serverHostName || insurance?.hostPortNumber;
  };

  const removeInsurance = (params) => {
    const allData = cloneDeep(rowDataForAll);
    const currentData = cloneDeep(rowData);

    const insurance = currentData.find(
      (e) => e.insuranceCompanyId === params.insuranceCompanyId
    );

    remove(
      currentData,
      (e) => e.insuranceCompanyId === insurance.insuranceCompanyId
    );

    setRowData([...currentData]);

    setRowDataForAll(
      sortBy(
        [
          ...allData,
          {
            ...insurance?.insuranceCompany,
            lastMntDateTime: insurance?.lastMntDateTime,
          },
        ],
        "companyName"
      )
    );

    setCurrentCoverage(currentCoverage - insurance?.riskPercentRate);
    setRisk(100);
  };

  const addInsurance = () => {
    setErrors(null);

    if (parseFloat(risk) + parseFloat(currentCoverage) > 100) {
      setErrors({
        errorMessage: labels?.productInsurer?.riskPercentageCannotBeGreater,
      });
      scrollToTop();
      return;
    }

    const insurance = rowDataForAll.find(
      (e) => e.id === selectedInsuranceFromAll
    );

    if (hasValidNetworkConfiguration(insurance)) {
      setCurrentCoverage(parseFloat(currentCoverage) + parseFloat(risk));
      remove(rowDataForAll, (e) => e.id === insurance.id);
      setRowDataForAll([...rowDataForAll]);

      if (rowDataForAll.length) {
        setSelectedInsuranceFromAll(rowDataForAll[0].id);
      } else {
        setSelectedInsuranceFromAll("");
      }

      setRowData([
        ...rowData,
        {
          remainderTo: false,
          insuranceCompany: insurance,
          riskPercentRate: risk,
          insuranceCompanyId: insurance?.id,
          effectiveDate: getFormattedDate(new Date()),
          lastMntDateTime: insurance?.lastMntDateTime,
        },
      ]);

      setFilterBy("");
      setRisk(100);
    } else {
      setErrors({
        errorMessage: labels?.productInsurer?.networkConfigError,
      });
      scrollToTop();
      return;
    }
  };

  const submitInsurance = () => {
    scrollToTop();
    const payload = {
      productId: location?.state?.productId,
      productSponsorId: location?.state?.productSponsorId,
      productInsurers: rowData,
    };

    saveProductInsuranceCompaniesApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  useEffect(() => {
    if (insuranceCompanies && insuranceCompanies?.productInsurers) {
      setRowData(cloneDeep(insuranceCompanies?.productInsurers));
      if (insuranceCompanies?.productInsurers?.length) {
        setCurrentCoverage(100);
      }
    }
  }, [insuranceCompanies]);

  useEffect(() => {
    if (
      allInsuranceCompanies &&
      allInsuranceCompanies?.insuranceCompanies &&
      insuranceCompanies &&
      insuranceCompanies?.productInsurers
    ) {
      const filteredInsurances =
        allInsuranceCompanies?.insuranceCompanies.filter(
          (e) =>
            !insuranceCompanies.productInsurers.find(
              (e1) => e.id === e1.insuranceCompany.id
            )
        );
      setRowDataForAll(cloneDeep(filteredInsurances));
      setSelectedInsuranceFromAll(filteredInsurances[0]?.id);
    }
  }, [allInsuranceCompanies, insuranceCompanies]);

  useEffect(() => {
    setPageHeader(labels?.productInsurer?.title + location?.state?.name);
    setNavHeader(labels?.navbar?.product);
  }, []);

  return (
    <section className="container-x-large py-4">
      {(isFetching || isFetching1 || isFetching2) && <Spinner />}
      <>
        {!readOnlyAccess && (
          <InfoBox
            wrapperClassName="mb-5"
            message={labels?.productInsurer?.selectInsurer}
          />
        )}

        <ErrorMessage
          error={errors || error || error1 || error2}
          wrapperClassName="mb-3"
        />

        {!readOnlyAccess && (
          <>
            <Heading
              text={labels?.productInsurer?.insuranceCompany}
              wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
              title={false}
            />

            <InputField
              id="filterBy"
              label={labels?.productInsurer?.filterByInsurance}
              wrapperClassName="col-md-3 mb-2"
              maxLength="35"
              change={(e) => {
                setFilterBy(e);
                setSelectedInsuranceFromAll("");
              }}
              value={filterBy}
            />

            <AgGrid
              wrapperClassNames="mb-3 d-md-block d-none"
              printClassNames="mt-md-n3"
              columnDefs={columnDefsForAll}
              rowData={
                filterBy
                  ? rowDataForAll.filter((e) =>
                      e.companyName
                        .toLowerCase()
                        .startsWith(filterBy.toLowerCase())
                    )
                  : rowDataForAll
              }
              domLayout={"normal"}
              getRowClass={getRowClassForAll}
              onRowClicked={onRowClickedForAll}
            />
            <div className="mb-2">
              <MobileResponsiveGrid
                key="product-insurance-companies-all"
                rowData={
                  filterBy
                    ? rowDataForAll.filter((e) =>
                        e.companyName
                          .toLowerCase()
                          .startsWith(filterBy.toLowerCase())
                      )
                    : rowDataForAll
                }
                columnDefs={columnDefsForAll}
                showSortDropDown={false}
                getRowClass={getRowClassForAll}
                onRowClick={onRowClickedForAll}
              />
            </div>
            <Heading
              text={labels?.productInsurer?.addCoverageRisk}
              wrapperClassNames="h4 border-bottom-1 pb-2 mt-5 mb-3"
              title={false}
            />
            <div>{labels?.productInsurer?.riskPercentage}</div>
            <div className="d-flex mb-5">
              <InputField
                id="percentage"
                wrapperClassName="d-flex align-items-center justify-content-end col-md-2 me-2"
                percentage={true}
                decimal={true}
                inputClass="button-height me-1"
                type="number"
                blur={(e) => setRisk(e)}
                value={risk}
                min={1}
                ariaLabel="Risk Percentage"
              />
              <Button
                id="add"
                text={labels?.productInsurer?.add}
                wrapperClassName="col-md-1 mx-3"
                click={addInsurance}
                disabled={
                  parseFloat(currentCoverage) == "100" ||
                  !selectedInsuranceFromAll
                }
              />
            </div>
          </>
        )}

        {!readOnlyAccess && (
          <>
            <Heading
              text={labels?.productInsurer?.riskCoverage}
              wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
              title={false}
            />

            <div className="d-flex justify-content-end mb-2">
              <div className="col-4 col-md-2 risk-label">
                {labels?.productInsurer?.coverageNeeded} <br />{" "}
                <span className="risk-value">100%</span>
              </div>
              <div className="col-4 col-md-2 risk-label">
                {labels?.productInsurer?.currentCoverage} <br />{" "}
                <span className="risk-value-1">
                  {addPercentage(currentCoverage)}
                </span>
              </div>
            </div>
          </>
        )}

        {readOnlyAccess && !rowData.length ? (
          <div>{labels?.productInsurer?.noAllInsuranceData}</div>
        ) : (
          <AgGrid
            wrapperClassNames="mb-4  d-md-block d-none"
            agGridClassNames="w-1400"
            columnDefs={columnDefsForInsurance}
            rowData={rowData}
            domLayout={"normal"}
            showPrintTable={false}
            overlayNoRowsTemplate={labels?.productInsurer?.noData}
          />
        )}

        <div className="mb-2">
          <MobileResponsiveGrid
            rowData={rowData}
            columnDefs={columnDefsForInsurance}
            showSortDropDown={false}
          />
        </div>

        {!readOnlyAccess && (
          <Button
            id="submit"
            text={labels?.productInsurer?.submit}
            wrapperClassName="col-md-2 ms-auto"
            click={submitInsurance}
            disabled={currentCoverage != 100}
          />
        )}

        <SuccessPopup
          showModal={showPopup}
          buttonText={labels?.productInsurer?.continue}
          click={cancel}
          body={labels?.productInsurer?.requestCompleted}
        />
      </>
    </section>
  );
}
